// src/components/Events/EventsSection.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { LuMapPin } from "react-icons/lu"; 
import './EventsSection.css';

const EventsSection = ({ userRole }) => {
    const [featuredEvents, setFeaturedEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    
    const trackRef = useRef(null);
    const slideIntervalRef = useRef(null);
    const containerRef = useRef(null);
    const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL;

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                setIsLoading(true);
                setError(null);
                
                const featuredRes = await api.get('/events/featured');

                const processEventData = (event) => ({
                    ...event,
                    banner: {
                        background: buildImageUrl(event.banner?.background) || 
                                  `${UPLOADS_URL}/uploads/default/banner.png`,
                        logo: buildImageUrl(event.banner?.logo) || 
                             `${UPLOADS_URL}/uploads/default/logo.png`,
                        text: event.banner?.text || 'Evento Próximamente',
                        buttonText: event.banner?.buttonText || 'Inscribirse',
                    }
                });

                setFeaturedEvents(featuredRes.data.map(processEventData));
                
            } catch (err) {
                console.error('Error loading events:', err);
                setError(err.message || 'Error al cargar los eventos');
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchEvents();
    }, []);

    useEffect(() => {
        if (featuredEvents.length > 0 && trackRef.current) {
            setupSlider();
        }

        return () => {
            if (slideIntervalRef.current) {
                clearInterval(slideIntervalRef.current);
            }
        };
    }, [featuredEvents]);

    const buildImageUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        return path.startsWith('/')
            ? `${UPLOADS_URL}${path}`
            : `${UPLOADS_URL}/${path}`;
    };

    const setupSlider = () => {
        const track = trackRef.current;
        const cards = Array.from(track.children);
        
        if (cards.length === 0) return;
    
        const cardWidth = cards[0].offsetWidth + 30;
        
        // Clonar suficientes elementos para slider infinito
        const totalClones = Math.max(4, cards.length);
        for (let i = 0; i < totalClones; i++) {
            const originalIndex = i % cards.length;
            const clone = cards[originalIndex].cloneNode(true);
            track.appendChild(clone);
        }
        
        let currentPosition = 0;
        let autoSlideInterval;
        
        const autoSlide = () => {
            currentPosition -= cardWidth;
            track.style.transition = 'transform 0.6s ease-out';
            track.style.transform = `translateX(${currentPosition}px)`;
    
            const totalCards = track.children.length;
            const maxPosition = -(cards.length * cardWidth);
            
            if (Math.abs(currentPosition) >= Math.abs(maxPosition)) {
                setTimeout(() => {
                    track.style.transition = 'none';
                    currentPosition = 0;
                    track.style.transform = `translateX(0)`;
                }, 600);
            }
        };
        
        autoSlideInterval = setInterval(autoSlide, 4000);
        slideIntervalRef.current = autoSlideInterval;
        
        const container = containerRef.current;
        if (container) {
            const pauseAutoSlide = () => {
                if (slideIntervalRef.current) {
                    clearInterval(slideIntervalRef.current);
                    slideIntervalRef.current = null;
                }
            };
            
            const resumeAutoSlide = () => {
                if (!slideIntervalRef.current) {
                    slideIntervalRef.current = setInterval(autoSlide, 4000);
                }
            };
            
            container.addEventListener('mouseenter', pauseAutoSlide);
            container.addEventListener('mouseleave', resumeAutoSlide);
        }
    };
    
    const handleSlide = (direction) => {
        const track = trackRef.current;
        if (!track) return;
        
        const cards = Array.from(track.children);
        if (cards.length === 0) return;
        
        if (slideIntervalRef.current) {
            clearInterval(slideIntervalRef.current);
            slideIntervalRef.current = null;
        }
        
        const cardWidth = cards[0].offsetWidth + 30;
        let currentTransform = track.style.transform;
        let currentPosition = 0;
        
        if (currentTransform && currentTransform !== 'none') {
            const match = currentTransform.match(/translateX\((-?\d+(?:\.\d+)?)px\)/);
            if (match) {
                currentPosition = parseFloat(match[1]);
            }
        }
        
        track.style.transition = 'transform 0.5s ease-out';
        
        if (direction === 'prev') {
            currentPosition += cardWidth;
            if (currentPosition > 0) {
                const totalWidth = (cards.length - 4) * cardWidth;
                currentPosition = -totalWidth;
            }
        } else {
            currentPosition -= cardWidth;
            const maxPosition = -(cards.length - 4) * cardWidth;
            if (currentPosition < maxPosition) {
                currentPosition = 0;
            }
        }
        
        track.style.transform = `translateX(${currentPosition}px)`;
        
        setTimeout(() => {
            if (!slideIntervalRef.current) {
                const originalCards = featuredEvents.length;
                const cardWidth = cards[0].offsetWidth + 30;
                let pos = 0;
                
                const autoSlide = () => {
                    pos -= cardWidth;
                    track.style.transition = 'transform 0.6s ease-out';
                    track.style.transform = `translateX(${pos}px)`;
                    
                    const maxPosition = -(originalCards * cardWidth);
                    if (Math.abs(pos) >= Math.abs(maxPosition)) {
                        setTimeout(() => {
                            track.style.transition = 'none';
                            pos = 0;
                            track.style.transform = `translateX(0)`;
                        }, 600);
                    }
                };
                
                slideIntervalRef.current = setInterval(autoSlide, 4000);
            }
        }, 5000);
    };

    if (isLoading) return <div className="loading" style={{textAlign: 'center', padding: '40px'}}>Cargando eventos destacados...</div>;
    if (error) return <div className="error" style={{textAlign: 'center', padding: '40px', color: 'red'}}>{error}</div>;
    if (featuredEvents.length === 0) return null;

    return (
        <section className="events-section">
            <div className="section-header">
                {userRole === 'Superadministrador' && (
                    <button 
                        onClick={() => navigate('/admin/featured-events')}
                        className="admin-panel-btn"
                    >
                        Panel de Administración
                    </button>
                )}
            </div>
            
            <div className="featured-events-slider-container" ref={containerRef}>
                <div 
                    className="featured-events-track" 
                    ref={trackRef}
                    style={{ transform: 'translateX(0)' }}
                >
                    {featuredEvents.map((event, index) => (
                        <EventCard key={`${event.slug}-${index}`} event={event} />
                    ))}
                </div>
                
                {featuredEvents.length > 3 && (
                    <div className="slider-controls">
                        <button className="slider-btn prev" onClick={() => handleSlide('prev')}>
                            ‹
                        </button>
                        <button className="slider-btn next" onClick={() => handleSlide('next')}>
                            ›
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

const getFormattedDateParts = (dateString) => {
    if (!dateString) return { day: '00', month: '---' };
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return { day: '00', month: '---' };
        
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleString('es-ES', { month: 'short' }).replace('.', '').toUpperCase();
        
        return { day, month };
    } catch (e) {
        return { day: '00', month: '---' };
    }
};


// EventsSection.js - Modificar EventCard

const EventCard = ({ event }) => {
    const getDisplayInfo = () => {
        const status = event.event_status;
        
        if (status === 'cancelled') {
            return {
                showDate: false,
                badgeText: 'CANCELADO',
                badgeClass: 'status-cancelled',
                message: event.status_message || 'Evento cancelado'
            };
        }
        
        if (status === 'rescheduled') {
            return {
                showDate: !!event.eventDate,
                badgeText: 'REPROGRAMADO',
                badgeClass: 'status-rescheduled',
                message: event.status_message || 'Nueva fecha por confirmar'
            };
        }
        
        if (status === 'tbd') {
            return {
                showDate: false,
                badgeText: 'PRÓXIMAMENTE',
                badgeClass: 'status-tbd',
                message: event.status_message || 'Fecha por confirmar'
            };
        }
        
        return {
            showDate: true,
            badgeText: null,
            badgeClass: null,
            message: null
        };
    };

    const getLocationText = () => {
        if (event.location_name) {
            return event.location_name;
        }
        if (event.location_city) {
            return event.location_city;
        }
        return "Ubicación por confirmar";
    };
    
    const locationText = getLocationText();
    const displayInfo = getDisplayInfo();
    
    // Usar eventDate o end_date como prioridad
    const effectiveDate = event.end_date || event.event_date;
    const { day, month } = getFormattedDateParts(effectiveDate);
    
    return (
        <Link to={`/${event.slug}`} className="event-card-link-wrapper">
            <div className="event-card-modern">
                <img 
                    src={event.banner?.background} 
                    alt={event.slug || event.title || 'Evento'} 
                    className="event-image-modern" 
                    onError={(e) => {
                        e.target.src = `${process.env.REACT_APP_UPLOADS_URL || 'http://localhost:5000'}/uploads/default/banner.png`;
                    }}
                />
                <div className="event-overlay-modern"></div>
                
                
                    {displayInfo.badgeText && (
                        <div className={`event-status-badge ${displayInfo.badgeClass}`}>
                            {displayInfo.badgeText}
                        </div>
                    )}
                    {event.banner?.logo && !event.banner.logo.includes('default/logo.png') && (
                    <img 
                        src={event.banner.logo}
                        alt={`Logo ${event.title}`}
                        className="event-logo-modern"
                    />
                )}
                
                <div className="event-meta-floating-box">    
                    {displayInfo.showDate ? (
                        <div className="event-date-badge">
                            <span className="badge-day">{day}</span>
                            <span className="badge-month">{month}</span>
                        </div>
                    ) : (
                        <div className="event-status-message">
                            {displayInfo.message}
                        </div>
                    )}
                    
                    <div className="event-details-text">
                        <h3 className="event-title-text">
                            {event.slug || event.title || 'Evento sin título'}
                        </h3>
                        
                        <div className="event-location-info">
                            <LuMapPin className="location-icon" />
                            <span className="location-text"> {locationText}</span>
                        </div>
                        
                        {displayInfo.message && displayInfo.showDate && (
                            <p className="event-status-note">{displayInfo.message}</p>
                        )}
                    </div>
                </div>
                
                {event.location_map_url && (
                    <div 
                        className="event-map-link"
                        onClick={(e) => {
                            e.preventDefault();
                            window.open(event.location_map_url, '_blank');
                        }}
                    >
                        <span>Ver mapa →</span>
                    </div>
                )}
            </div>
        </Link>
    );
};

export default EventsSection;